import React, { FC, Fragment, useContext, useState } from "react"
import { Input, message, Tag } from "antd"
import { SearchOutlined } from "@ant-design/icons"
import "../../../../styles/user.admin.scss"
import user from "../../../../assets/user-img.png"
import { Link, useNavigate, useParams } from "react-router-dom"
import { isAuth, SidebarContext } from "../../../auth/ProtectedRoute"
// import { InstructorDetail } from "./type/DataType"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import axios from "axios"

interface Language {
  id: number
  name: string
  code: string
}

interface Topic {
  id: number
  learning_instructor_id: number
  name: string
  created_at: string
  updated_at: string
}

interface InstructorDetail {
  id: number
  fullname: string
  email: string
  phone: string | null
  language: Language
  profession: string
  member_profile_code: string
  topic: Topic[]
  created_at: string
  updated_at: string
  status: string
}
interface ApprovalResponse {
  response: boolean
  status: string
  message: string
  code: number
}

interface ApiResponse {
  response: boolean
  status: string
  message: string
  code: number
  data: InstructorDetail
}

const ViewInstructor: FC = () => {
  const { userName, sidebarData } = useContext(SidebarContext)
  const params = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const { data, status, error } = useQuery<ApiResponse>({
    queryKey: ["instructorDetail"],
    queryFn: async () => {
      const { data } = await axios.get(
        `${process.env.REACT_APP_HOST_API}learning/instructors/details/?instructor_id=${params.id}`,
        {
          headers: {
            Authorization: `Bearer ${isAuth()}`,
          },
        }
      )
      return data
    },
  })

  const approveMutation = useMutation<ApprovalResponse, Error>({
    mutationFn: async () => {
      const { data } = await axios.post(
        `${process.env.REACT_APP_HOST_API}learning/instructor/approve/?instructor_id=${params.id}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${isAuth()}`,
          },
        }
      )
      return data
    },
    onSuccess: data => {
      if (data.status === "SUCCESS") {
        message.success("Instructor approved successfully")
      } else if (data.message.toLowerCase().includes("already approved")) {
        message.info("Instructor has already been approved")
      } else {
        message.warning(data.message || "Unexpected response from server")
      }
      queryClient.invalidateQueries({ queryKey: ["instructorDetail", params.id] })
    },
    onError: error => {
      message.error("Failed to approve instructor")
      console.error("Approve error:", error)
    },
  })

  const disapproveMutation = useMutation<ApprovalResponse, Error>({
    mutationFn: async () => {
      const { data } = await axios.post(
        `${process.env.REACT_APP_HOST_API}learning/instructor/reject/?instructor_id=${params.id}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${isAuth()}`,
          },
        }
      )
      return data
    },
    onSuccess: data => {
      if (data.status === "SUCCESS") {
        message.success("Instructor disapproved successfully")
      } else {
        message.warning(data.message || "Unexpected response from server")
      }
      queryClient.invalidateQueries({ queryKey: ["instructorDetail", params.id] })
    },
    onError: error => {
      message.error("Failed to disapprove instructor")
      console.error("Disapprove error:", error)
    },
  })

  if (status === "error") {
    console.error(error)
  }

  const instructorData = data?.data

  const getStatusText = (status: string) => {
    switch (status) {
      case "0":
        return "Pending"
      case "1":
        return "Approved"
      case "2":
        return "Disapproved"
      default:
        return "Unknown"
    }
  }

  // Check if the instructor's status allows for approval/disapproval actions
  const canPerformActions = instructorData?.status === "0"

  if (!instructorData) {
    return <div>No instructor data found.</div>
  }

  const handleApprove = () => {
    approveMutation.mutate()
  }

  const handleDisapprove = () => {
    disapproveMutation.mutate()
  }

  return (
    <Fragment>
      <div className="user">
        <div className="user_heading">
          <div className="user_heading-icon">
            <div>
              <svg
                width="28"
                height="26"
                viewBox="0 0 28 26"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M20.25 21.0372H15.25L9.68748 24.7371C8.86248 25.2871 7.75 24.6997 7.75 23.6997L7.75 21.0372C4 21.0372 1.5 18.5372 1.5 14.7872L1.5 7.28711C1.5 3.53711 4 1.03711 7.75 1.03711L20.25 1.03711C24 1.03711 26.5 3.53711 26.5 7.28711L26.5 14.7872C26.5 18.5372 24 21.0372 20.25 21.0372Z"
                  stroke="#858585"
                  strokeWidth="1.5"
                  strokeMiterlimit="10"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <div>
              <svg
                width="30"
                height="30"
                viewBox="0 0 30 30"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M15.0262 3.63672C10.8887 3.63672 7.52621 6.99922 7.52621 11.1367L7.52621 14.7492C7.52621 15.5117 7.20121 16.6742 6.81371 17.3242L5.37621 19.7117C4.48871 21.1867 5.10121 22.8242 6.72621 23.3742C12.1137 25.1742 17.9262 25.1742 23.3137 23.3742C24.8262 22.8742 25.4887 21.0867 24.6637 19.7117L23.2262 17.3242C22.8512 16.6742 22.5262 15.5117 22.5262 14.7492V11.1367C22.5262 7.01172 19.1512 3.63672 15.0262 3.63672Z"
                  stroke="#858585"
                  strokeWidth="1.5"
                  strokeMiterlimit="10"
                  strokeLinecap="round"
                />
                <path
                  d="M17.3359 4.00078C16.9484 3.88828 16.5484 3.80078 16.1359 3.75078C14.9359 3.60078 13.7859 3.68828 12.7109 4.00078C13.0734 3.07578 13.9734 2.42578 15.0234 2.42578C16.0734 2.42578 16.9734 3.07578 17.3359 4.00078Z"
                  stroke="#858585"
                  strokeWidth="1.5"
                  strokeMiterlimit="10"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M18.7734 23.8242C18.7734 25.8867 17.0859 27.5742 15.0234 27.5742C13.9984 27.5742 13.0484 27.1492 12.3734 26.4742C11.6984 25.7992 11.2734 24.8492 11.2734 23.8242"
                  stroke="#858585"
                  strokeWidth="1.5"
                  strokeMiterlimit="10"
                />
              </svg>
            </div>
            <div>
              <img src={user} alt="profile" />
            </div>
          </div>
        </div>
        <div
          className="user-title"
          style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}
        >
          <h1>Users</h1>
          <div className="project-add--heading-btn" style={{ margin: "10px 0" }}>
            <button onClick={() => navigate(-1)}>
              <span>
                <svg
                  width="22"
                  height="20"
                  viewBox="0 0 22 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M7 2L2 7L7 12"
                    stroke="white"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M2 7H10C15.523 7 20 11.477 20 17V18"
                    stroke="white"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
              Back
            </button>
          </div>
        </div>
        <div className="user_header">
          <div className="user_header-wrap" style={{ display: "none" }}>
            <div className="user_header-wrap-btn">
              <div>
                <Link to="add-user" state={{ userName: userName, sidebar: sidebarData }}>
                  <button>Add User</button>
                </Link>
              </div>

              <div className="edit">
                <span>
                  <svg
                    width="30"
                    height="30"
                    viewBox="0 0 30 30"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M8.32267 22.5C8.20844 22.4998 8.09551 22.4758 7.99105 22.4296C7.88659 22.3834 7.79288 22.316 7.71589 22.2316C7.63751 22.1479 7.57771 22.0486 7.54039 21.9402C7.50307 21.8318 7.48909 21.7168 7.49937 21.6026L7.70107 19.3846L17.0192 10.0698L19.9312 12.981L10.6156 22.295L8.39759 22.4967C8.37268 22.499 8.34768 22.5001 8.32267 22.5ZM20.5125 12.3989L17.6013 9.48769L19.3475 7.74146C19.424 7.66492 19.5148 7.60419 19.6147 7.56276C19.7147 7.52133 19.8218 7.5 19.93 7.5C20.0382 7.5 20.1453 7.52133 20.2453 7.56276C20.3452 7.60419 20.436 7.66492 20.5125 7.74146L22.2587 9.48769C22.3353 9.56415 22.396 9.65495 22.4374 9.7549C22.4789 9.85485 22.5002 9.96198 22.5002 10.0702C22.5002 10.1784 22.4789 10.2855 22.4374 10.3855C22.396 10.4854 22.3353 10.5762 22.2587 10.6527L20.5133 12.3981L20.5125 12.3989Z"
                      fill="#666666"
                    />
                  </svg>
                </span>
                <Link
                  to="edit-user"
                  state={{ userName: userName, sidebar: sidebarData }}
                  className="rclv"
                >
                  Edit
                </Link>
              </div>
              <div className="delet">
                <span>
                  <svg
                    width="30"
                    height="30"
                    viewBox="0 0 30 30"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M15.0003 7.5C15.6152 7.49996 16.2068 7.73523 16.6538 8.15753C17.1008 8.57984 17.3692 9.15718 17.404 9.77111L17.4077 9.90741H21.2966C21.4373 9.90745 21.5728 9.96092 21.6757 10.057C21.7785 10.1531 21.8411 10.2847 21.8507 10.4251C21.8603 10.5655 21.8162 10.7044 21.7274 10.8136C21.6385 10.9227 21.5116 10.9941 21.3721 11.0133L21.2966 11.0185H20.707L19.7588 20.663C19.7113 21.1438 19.4944 21.5921 19.1469 21.9278C18.7994 22.2634 18.3439 22.4647 17.8618 22.4956L17.7314 22.5H12.2692C11.7858 22.5 11.3182 22.3281 10.9499 22.015C10.5816 21.702 10.3367 21.2682 10.2588 20.7911L10.2418 20.6622L9.29288 11.0185H8.70399C8.56974 11.0185 8.44004 10.9699 8.33886 10.8817C8.23768 10.7934 8.17188 10.6715 8.15362 10.5385L8.14844 10.463C8.14844 10.3287 8.19706 10.199 8.2853 10.0978C8.37354 9.99665 8.49543 9.93085 8.62844 9.91259L8.70399 9.90741H12.5929C12.5929 9.26892 12.8465 8.65659 13.298 8.20511C13.7495 7.75364 14.3618 7.5 15.0003 7.5ZM19.5907 11.0185H10.4092L11.3477 20.5533C11.3685 20.7661 11.4622 20.9651 11.613 21.1166C11.7639 21.2681 11.9624 21.3627 12.1751 21.3844L12.2692 21.3889H17.7314C18.1758 21.3889 18.5529 21.0741 18.6388 20.6467L18.6536 20.5533L19.5899 11.0185H19.5907ZM16.2966 13.0556C16.4308 13.0556 16.5605 13.1042 16.6617 13.1924C16.7629 13.2807 16.8287 13.4026 16.847 13.5356L16.8521 13.6111V18.7963C16.8521 18.9371 16.7986 19.0725 16.7025 19.1754C16.6064 19.2783 16.4749 19.3408 16.3345 19.3504C16.194 19.36 16.0552 19.3159 15.946 19.2271C15.8368 19.1383 15.7654 19.0113 15.7462 18.8719L15.741 18.7963V13.6111C15.741 13.4638 15.7996 13.3225 15.9037 13.2183C16.0079 13.1141 16.1492 13.0556 16.2966 13.0556ZM13.704 13.0556C13.8382 13.0556 13.9679 13.1042 14.0691 13.1924C14.1703 13.2807 14.2361 13.4026 14.2544 13.5356L14.2595 13.6111V18.7963C14.2595 18.9371 14.206 19.0725 14.1099 19.1754C14.0138 19.2783 13.8823 19.3408 13.7419 19.3504C13.6014 19.36 13.4626 19.3159 13.3534 19.2271C13.2442 19.1383 13.1728 19.0113 13.1536 18.8719L13.1484 18.7963V13.6111C13.1484 13.4638 13.207 13.3225 13.3112 13.2183C13.4153 13.1141 13.5567 13.0556 13.704 13.0556ZM15.0003 8.61111C14.675 8.61112 14.3615 8.73346 14.1222 8.95384C13.8829 9.17421 13.7352 9.47652 13.7084 9.80074L13.704 9.90741H16.2966C16.2966 9.56361 16.16 9.23389 15.9169 8.99079C15.6738 8.74768 15.3441 8.61111 15.0003 8.61111Z"
                      fill="#666666"
                    />
                  </svg>
                </span>
                <p>Delete</p>
              </div>
            </div>
            <div className="user_header-wrap-search">
              <div>
                <Input
                  size="large"
                  placeholder="Search all.."
                  prefix={<SearchOutlined />}
                  className="inp"
                />
              </div>
              <div className="filter">
                <span>
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M14.7962 0.5H1.20394C0.579391 0.5 0.264245 1.25779 0.706774 1.70032L6.125 7.11936V13.1562C6.125 13.3857 6.23694 13.6007 6.42491 13.7323L8.76866 15.3723C9.23105 15.696 9.87499 15.368 9.87499 14.7963V7.11936L15.2934 1.70032C15.735 1.25867 15.422 0.5 14.7962 0.5Z"
                      fill="black"
                    />
                  </svg>
                </span>
                <p>Filter</p>
              </div>
            </div>
          </div>
          <div>
            <div className="user_klearning">
              <div className="user_klearning_wrapper">
                <div className="heading">
                  <div className="heading_left">
                    <div className="heading_left_img">
                      <img src={user} alt="profile" />
                      <div className="heading_left_img_desc">
                        <h2>{instructorData?.fullname}</h2>
                        <div className="details">
                          <p>User Id: {instructorData?.id}</p>
                          <h6>{instructorData?.profession}</h6>
                          <a
                            href={`${process.env.REACT_APP_KOMINITI_URL}`}
                            target="_blank"
                            rel="noreferrer"
                            className="user_name"
                          >
                            {`${process.env.REACT_APP_KOMINITI_URL}`.trim()}
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="heading_right">
                    <span>{getStatusText(instructorData.status)}</span>
                  </div>
                </div>
                <div className="sub-header">
                  <div className="email">
                    <span>
                      <svg
                        width="40"
                        height="40"
                        viewBox="0 0 40 40"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <circle cx="20" cy="20" r="20" fill="#F0F7FE" />
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M14.8148 14C13.8125 14 13 14.8679 13 15.9385V24.0615C13 25.1321 13.8125 26 14.8148 26H25.1852C26.1875 26 27 25.1321 27 24.0615V15.9385C27 14.8679 26.1875 14 25.1852 14H14.8148ZM14.5556 16.5988V24.0615C14.5556 24.2145 14.6716 24.3385 14.8148 24.3385H25.1852C25.3284 24.3385 25.4444 24.2145 25.4444 24.0615V16.5988L21.1024 20.1454C20.4517 20.6769 19.5483 20.6769 18.8976 20.1454L14.5556 16.5988ZM24.0311 15.6615H15.9689L19.8425 18.8255C19.9355 18.9015 20.0645 18.9015 20.1575 18.8255L24.0311 15.6615Z"
                          fill="#4895EF"
                        />
                      </svg>
                    </span>
                    <div className="email_em">
                      <h2>{instructorData?.email}</h2>
                      <p>Email</p>
                    </div>
                  </div>
                  <div className="phone">
                    <svg
                      width="40"
                      height="40"
                      viewBox="0 0 40 40"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <circle cx="20" cy="20" r="20" fill="#F0F7FE" />
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M13.0002 14.7168C13.0063 13.7068 13.8511 13 14.7648 13H16.2208C17.049 13 17.7722 13.563 17.9784 14.3683L18.4022 16.0231C18.5878 16.7479 18.3129 17.513 17.7092 17.9521L15.5865 19.4956C16.4931 21.448 18.0932 23.3045 20.775 24.4228L22.3896 22.4123C22.8441 21.8463 23.5904 21.6058 24.2883 21.8004L25.671 22.1861C26.4565 22.4052 27 23.1231 27 23.9415V25.2021C27 26.2169 26.1566 27.0794 25.0946 26.9942C23.458 26.8629 22.0194 26.5492 20.759 26.0912C17.0281 24.7355 14.9463 22.1494 13.903 19.5191C13.2344 17.8334 12.9917 16.1317 13.0002 14.7168ZM22.3455 24.9553C23.206 25.1865 24.1605 25.3527 25.2186 25.4376C25.3216 25.4458 25.4444 25.3606 25.4444 25.2021V23.9415C25.4444 23.8246 25.3668 23.722 25.2546 23.6907L23.8718 23.305C23.7721 23.2772 23.6655 23.3115 23.6006 23.3924L22.3455 24.9553ZM15.0181 17.9807L16.7966 16.6874C16.8829 16.6247 16.9221 16.5154 16.8956 16.4119L16.4719 14.757C16.4424 14.642 16.3391 14.5616 16.2208 14.5616H14.7648C14.6954 14.5616 14.6391 14.5879 14.6033 14.6214C14.5703 14.6523 14.556 14.6866 14.5557 14.7262C14.5499 15.6966 14.68 16.8267 15.0181 17.9807Z"
                        fill="#4895EF"
                      />
                    </svg>

                    <div className="phone_em">
                      <h2>{instructorData?.phone}</h2>
                      <p>Phone Number</p>
                    </div>
                  </div>
                  <div className="addr">
                    <svg
                      width="40"
                      height="40"
                      viewBox="0 0 40 40"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <circle cx="20" cy="20" r="20" fill="#F0F7FE" />
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M17.2095 15.7967C17.9611 14.9771 18.9664 14.5299 20 14.5299C21.0336 14.5299 22.0389 14.9771 22.7905 15.7967C23.544 16.6184 23.9769 17.7454 23.9769 18.9325C23.9769 19.3244 23.8503 19.8273 23.595 20.4206C23.3435 21.0054 22.988 21.6302 22.581 22.2523C21.767 23.4966 20.7861 24.6706 20.1367 25.4048C20.0946 25.4524 20.0455 25.4701 20 25.4701C19.9545 25.4701 19.9054 25.4524 19.8633 25.4048C19.2139 24.6706 18.233 23.4966 17.419 22.2523C17.012 21.6302 16.6565 21.0054 16.405 20.4206C16.1497 19.8273 16.0231 19.3244 16.0231 18.9325C16.0231 17.7454 16.456 16.6184 17.2095 15.7967ZM20 13C18.5202 13 17.115 13.6417 16.0893 14.7602C15.0655 15.8767 14.5 17.3784 14.5 18.9325C14.5 19.62 14.7123 20.3428 15.0068 21.0274C15.305 21.7205 15.7101 22.4259 16.1461 23.0925C17.0182 24.4254 18.0535 25.6622 18.7247 26.4209C19.4077 27.193 20.5923 27.193 21.2753 26.4209C21.9465 25.6622 22.9818 24.4254 23.8539 23.0925C24.2899 22.4259 24.695 21.7205 24.9932 21.0274C25.2877 20.3428 25.5 19.62 25.5 18.9325C25.5 17.3784 24.9345 15.8767 23.9107 14.7602C22.885 13.6417 21.4798 13 20 13ZM19.4077 19.2044C19.4077 18.8758 19.6729 18.6095 20 18.6095C20.3271 18.6095 20.5923 18.8758 20.5923 19.2044C20.5923 19.533 20.3271 19.7994 20 19.7994C19.6729 19.7994 19.4077 19.533 19.4077 19.2044ZM20 17.0796C18.8317 17.0796 17.8846 18.0309 17.8846 19.2044C17.8846 20.3779 18.8317 21.3292 20 21.3292C21.1683 21.3292 22.1154 20.3779 22.1154 19.2044C22.1154 18.0309 21.1683 17.0796 20 17.0796Z"
                        fill="#4895EF"
                      />
                    </svg>

                    <div className="addr_em">
                      <h2>city of Gbogbo estate alayole ibadan ringroad</h2>
                      <p>Address</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="user_klearning_wrap">
                <div className="cate">
                  <div className="cate_right">
                    <h1>Topics Taught</h1>
                    <div className="cate_right_children">
                      <div className="list">
                        {instructorData?.topic.length > 0 &&
                          instructorData?.topic.map((topic, index) => (
                            <span key={index}>{topic.name}</span>
                          ))}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="cate_left">
                  <h1>Language</h1>
                  <div className="cate_left_children">
                    <div className="list">
                      <span>{instructorData?.language.name}</span>
                    </div>
                  </div>
                </div>
              </div>
              {canPerformActions && (
                <div className="buttons">
                  <div className="buttons_button">
                    <button
                      onClick={handleDisapprove}
                      disabled={disapproveMutation.isPending}
                      className="notaccpt"
                    >
                      {disapproveMutation.isPending ? "Disapproving..." : "Disapprove"}
                    </button>
                    <button
                      className="accept"
                      onClick={handleApprove}
                      disabled={approveMutation.isPending}
                    >
                      {approveMutation.isPending ? "Approving..." : "Approve"}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  )
}
export default ViewInstructor
